import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { createPublicMosqueEvaluation } from "../../../services/mosqueEvaluationService";
import type { CreatePublicMosqueEvaluationParams } from "../../../services/mosqueEvaluationService";
import { getEvaluationBrowserIdentifier } from "../../../utils/browserIdentifier";



const CLEANLINESS_OPTIONS = [
  { value: "", label: "— اختر —" },
  { value: "clean", label: "نظيف" },
  { value: "somehow_clean", label: "نظيف إلى حد ما" },
  { value: "dirty", label: "غير نظيف" },
];

const SUPPLIES_OPTIONS = [
  { value: "", label: "— اختر —" },
  { value: "available", label: "متوفر" },
  { value: "missing_some", label: "ناقص بعضها" },
  { value: "not_available", label: "غير متوفر" },
];

export default function MosqueEvaluationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const mosqueId = id ? parseInt(id, 10) : 0;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  /**
   * Used only to show validation message under the missing field.
   */
  const [fieldError, setFieldError] =
    useState<keyof CreatePublicMosqueEvaluationParams | null>(null);

  const [form, setForm] = useState<CreatePublicMosqueEvaluationParams>({
    mosque_id: mosqueId,

    /**
     * Evaluation is anonymous now.
     * For web, channel must always be "web".
     */
    channel: "web",

    /**
     * Stable browser identifier.
     * This is used by backend/Odoo for uniqueness:
     * mosque_id + channel + identifier
     */
    identifier: getEvaluationBrowserIdentifier(),

    imam_muezzin_status: "",
    staff_commitment: "",
    prayer_hall_cleanliness: "",
    restroom_cleanliness: "",
    basic_supplies_availability: "",
    notes: "",
  });

  const update = (
    field: keyof CreatePublicMosqueEvaluationParams,
    value: string | number
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError(null);
    setFieldError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setFieldError(null);
    setError(null);

    if (mosqueId <= 0) {
      setError("معرف المسجد غير صالح.");
      return;
    }

    const requiredEvaluationFields: {
      key: keyof CreatePublicMosqueEvaluationParams;
      label: string;
    }[] = [
      { key: "prayer_hall_cleanliness", label: "نظافة مصلى الرجال" },
      { key: "restroom_cleanliness", label: "نظافة دورات المياه" },
      { key: "basic_supplies_availability", label: "توفر المستلزمات الأساسية" },
    ];

    const missing = requiredEvaluationFields.find(
      (f) => !(form[f.key] as string)?.trim()
    );

    if (missing) {
      setFieldError(missing.key);
      return;
    }

    setLoading(true);

    try {
      await createPublicMosqueEvaluation({
        ...form,

        /**
         * Always enforce mosque ID from route.
         */
        mosque_id: mosqueId,

        /**
         * Always enforce anonymous web evaluation.
         */
        channel: "web",

        /**
         * Always refresh/read stable browser ID before submit.
         */
        identifier: getEvaluationBrowserIdentifier(),
      });

      setSuccess(true);
    } catch (err: any) {
      setError(err?.message ?? "حدث خطأ أثناء إرسال التقييم.");
    } finally {
      setLoading(false);
    }
  };

  const breadcrumb = [
    { label: "الرئيسية", to: "/" },
    { label: "المساجد", to: "/mosquesearch" },
    { label: "تفاصيل المسجد", to: `/mosques/${id}` },
    { label: "تقييم حالة المسجد", to: null },
  ];

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-8" dir="rtl">
        <div className="rounded-2xl border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30 p-6 text-center">
          <p className="text-green-800 dark:text-green-200 text-lg font-medium mb-4">
            تم إرسال تقييم المسجد بنجاح.
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/mosques/${id}`)}
              className="inline-flex h-11 items-center justify-center rounded-xl bg-sa-600 px-5 text-white font-[600] hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500"
            >
              العودة لتفاصيل المسجد
            </button>

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex h-11 items-center justify-center rounded-xl border border-neutral-300 bg-white px-5 text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-sa-500"
            >
              رجوع
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (mosqueId <= 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8" dir="rtl">
        <div className="rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 p-6">
          <p className="text-red-700 dark:text-red-300">
            معرف المسجد غير صالح.
          </p>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-4 rounded-xl border border-neutral-300 px-4 py-2 text-sm hover:bg-neutral-50 dark:border-neutral-600 dark:hover:bg-neutral-800"
          >
            رجوع
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6" dir="rtl">
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300">
          {breadcrumb.map((b, i) => (
            <li key={i} className="flex items-center">
              {b.to ? (
                <Link to={b.to} className="hover:text-sa-700 hover:underline">
                  {b.label}
                </Link>
              ) : (
                <span className="font-medium text-neutral-800 dark:text-neutral-100">
                  {b.label}
                </span>
              )}

              {b.to && <span className="mx-2 text-neutral-400">/</span>}
            </li>
          ))}
        </ol>
      </nav>

      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex h-11 items-center justify-center rounded-xl border border-neutral-300 bg-white px-5 text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-sa-500"
        >
          رجوع
        </button>
      </div>

      <section className="rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
        <header className="border-b border-neutral-200 px-5 py-3 dark:border-neutral-800">
          <h1 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 text-center">
            تقييم حالة المسجد
          </h1>
        </header>

        <div className="p-5">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30 px-4 py-3 text-red-700 dark:text-red-300 text-sm">
                <p>{error}</p>
              </div>
            )}

            {/* Evaluation fields only. User info and Yaqeen verification removed. */}
            <div>
              <h2 className="text-base font-medium text-neutral-800 dark:text-neutral-200 mb-3">
                تقييم الحالة
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                
                <div>
                  <label className="block text-sm text-neutral-600 dark:text-neutral-400 mb-1">
                    نظافة مصلى الرجال
                  </label>

                  <select
                    value={form.prayer_hall_cleanliness}
                    onChange={(e) =>
                      update("prayer_hall_cleanliness", e.target.value)
                    }
                    className="w-full rounded-xl border border-neutral-300 bg-white px-4 py-2.5 text-neutral-900 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 focus:border-sa-500 focus:ring-1 focus:ring-sa-500"
                  >
                    {CLEANLINESS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>

                  {fieldError === "prayer_hall_cleanliness" && (
                    <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                      يرجى اختيار خيار
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm text-neutral-600 dark:text-neutral-400 mb-1">
                    نظافة دورات المياه
                  </label>

                  <select
                    value={form.restroom_cleanliness}
                    onChange={(e) =>
                      update("restroom_cleanliness", e.target.value)
                    }
                    className="w-full rounded-xl border border-neutral-300 bg-white px-4 py-2.5 text-neutral-900 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 focus:border-sa-500 focus:ring-1 focus:ring-sa-500"
                  >
                    {CLEANLINESS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>

                  {fieldError === "restroom_cleanliness" && (
                    <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                      يرجى اختيار خيار
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm text-neutral-600 dark:text-neutral-400 mb-1">
                    توفر المستلزمات الأساسية
                  </label>

                  <select
                    value={form.basic_supplies_availability}
                    onChange={(e) =>
                      update("basic_supplies_availability", e.target.value)
                    }
                    className="w-full rounded-xl border border-neutral-300 bg-white px-4 py-2.5 text-neutral-900 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 focus:border-sa-500 focus:ring-1 focus:ring-sa-500"
                  >
                    {SUPPLIES_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>

                  {fieldError === "basic_supplies_availability" && (
                    <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                      يرجى اختيار خيار
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm text-neutral-600 dark:text-neutral-400 mb-1">
                ملاحظات إضافية
              </label>

              <textarea
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                rows={3}
                className="w-full rounded-xl border border-neutral-300 bg-white px-4 py-2.5 text-neutral-900 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 focus:border-sa-500 focus:ring-1 focus:ring-sa-500"
                placeholder="أي ملاحظات أخرى..."
              />
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex h-11 items-center justify-center rounded-xl bg-sa-600 px-6 text-white font-[600] hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "جاري الإرسال…" : "إرسال التقييم"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}